import {
  CommunicationContextType,
  ComposeContextIdKind,
  ComposeContextValueKind,
  IAdHocAudience,
  IComposeContextSelection
} from '../../../lib/types/communication.types';

// Shared row queries over a selection list; rows missing their id/value (e.g.
// chips restored from a draft after instance deletion) are dropped.
export const idsOfType = (contexts: IComposeContextSelection[], type: ComposeContextIdKind): string[] =>
  contexts
    .filter((row) => row.type === type)
    .map((row) => row.id)
    .filter((id): id is string => !!id);

export const valuesOfKind = (contexts: IComposeContextSelection[], kind: ComposeContextValueKind): string[] =>
  contexts
    .filter((row) => row.type === kind)
    .map((row) => row.value)
    .filter((value): value is string => !!value);

// Context chips -> AudienceCriteria. The selector allows at most one session row;
// if that guard ever slips, the first row wins (mirrored by resolveCommunicationContext).
export const buildAudienceFromSelections = (contexts: IComposeContextSelection[]): IAdHocAudience => {
  const sessionIds = idsOfType(contexts, CommunicationContextType.ASSESSMENT_SESSION);

  const audience: IAdHocAudience = {
    ageGroupIds: idsOfType(contexts, CommunicationContextType.AGE_GROUP),
    playerAssessmentIds: idsOfType(contexts, CommunicationContextType.PLAYER_ASSESSMENT),
    levels: valuesOfKind(contexts, ComposeContextValueKind.LEVEL),
    teams: valuesOfKind(contexts, ComposeContextValueKind.TEAM)
  };
  if (sessionIds.length) audience.sessionId = sessionIds[0];

  return audience;
};

// Most-specific target context wins: one player > session > one age group >
// assessment root. Level/Team rows are criteria-only and never anchor a context.
export const resolveCommunicationContext = (
  contexts: IComposeContextSelection[],
  assessmentId?: string
): { contextType?: CommunicationContextType; contextId?: string } => {
  const playerIds = idsOfType(contexts, CommunicationContextType.PLAYER_ASSESSMENT);
  if (playerIds.length === 1) {
    return { contextType: CommunicationContextType.PLAYER_ASSESSMENT, contextId: playerIds[0] };
  }

  const sessionIds = idsOfType(contexts, CommunicationContextType.ASSESSMENT_SESSION);
  if (sessionIds.length) {
    return { contextType: CommunicationContextType.ASSESSMENT_SESSION, contextId: sessionIds[0] };
  }

  const ageGroupIds = idsOfType(contexts, CommunicationContextType.AGE_GROUP);
  if (ageGroupIds.length === 1) {
    return { contextType: CommunicationContextType.AGE_GROUP, contextId: ageGroupIds[0] };
  }

  if (assessmentId) {
    return { contextType: CommunicationContextType.ASSESSMENT, contextId: assessmentId };
  }

  return {};
};
